import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  setDoc, 
  addDoc, 
  deleteDoc, 
  updateDoc, 
  onSnapshot,
  query,
  limit,
  startAfter,
  orderBy,
  writeBatch
} from "firebase/firestore";
import { 
  ref, 
  uploadBytesResumable, 
  getDownloadURL, 
  deleteObject 
} from "firebase/storage";
import { db, storage } from "../config/firebase";
import { Agent } from "../types";
import { SiteContent } from "../types/siteContent";

// Collections (Firestore is used for site_content, agents, and inquiries)
const AGENTS_COLLECTION = "agents";
const SITE_CONTENT_COLLECTION = "site_content";
const INQUIRIES_COLLECTION = "inquiries";

/**
 * Upload an image or video file directly to Firebase Storage with Cloudflare CDN readiness.
 * Returns download URL and storage path.
 * 
 * Progressive uploading tracks percentage.
 */
export async function uploadMediaToFirebase(
  file: File,
  folder: "properties" | "agents" | "branding" | "videos" = "properties",
  onProgress?: (progress: number) => void
): Promise<{ url: string; path: string }> {
  // Generate clean unique filename
  const cleanName = file.name.replace(/[^a-zA-Z0-9.-]/g, "_");
  const uniqueName = `${Date.now()}_${cleanName}`;
  const storagePath = `${folder}/${uniqueName}`;
  const storageRef = ref(storage, storagePath);

  // Set appropriate cache metadata for Cloudflare CDN edge caching (e.g. 1 month)
  const metadata = {
    contentType: file.type,
    cacheControl: "public, max-age=2592000, s-maxage=31536000",
    customMetadata: {
      uploadedAt: new Date().toISOString(),
      originalName: file.name
    }
  };

  const uploadTask = uploadBytesResumable(storageRef, file, metadata);

  return new Promise((resolve, reject) => {
    uploadTask.on(
      "state_changed",
      (snapshot) => {
        if (snapshot.totalBytes > 0 && onProgress) {
          const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
          onProgress(progress);
        }
      },
      (error) => {
        console.error("Firebase Storage upload error:", error);
        reject(error);
      },
      async () => {
        try {
          const downloadUrl = await getDownloadURL(uploadTask.snapshot.ref);
          resolve({ url: downloadUrl, path: storagePath });
        } catch (err) {
          reject(err);
        }
      }
    );
  });
}

/**
 * Delete a media file from Firebase Storage
 */
export async function deleteMediaFromFirebase(storagePath: string): Promise<void> {
  try {
    const storageRef = ref(storage, storagePath);
    await deleteObject(storageRef);
  } catch (error) {
    console.warn("Failed to delete media from Firebase Storage:", error);
  }
}

// ---------------------------------------------------------------------------
// AGENTS CRUD
// ---------------------------------------------------------------------------

export async function getAgentsFromFirebase(): Promise<Agent[]> {
  try {
    const snapshot = await getDocs(collection(db, AGENTS_COLLECTION));
    const items: Agent[] = [];
    snapshot.forEach((docSnap) => {
      items.push({ id: docSnap.id, ...(docSnap.data() as any) });
    });
    items.sort((a, b) => (Number(a.order ?? 999999) - Number(b.order ?? 999999)));
    return items;
  } catch (err) {
    console.warn("Could not fetch agents from Firebase:", err);
    return [];
  }
}

export function subscribeToAgents(callback: (agents: Agent[]) => void) {
  try {
    return onSnapshot(collection(db, AGENTS_COLLECTION), (snapshot) => {
      const items: Agent[] = [];
      snapshot.forEach((docSnap) => {
        items.push({ id: docSnap.id, ...(docSnap.data() as any) });
      });
      items.sort((a, b) => (Number(a.order ?? 999999) - Number(b.order ?? 999999)));
      callback(items);
    }, (error) => {
      console.warn("Agents subscription error:", error);
    });
  } catch (e) {
    console.warn("Failed subscribing to agents:", e);
    return () => {};
  }
}

export async function saveAgentToFirebase(agent: Agent): Promise<void> {
  try {
    const agentDoc = doc(db, AGENTS_COLLECTION, agent.id);
    await setDoc(agentDoc, agent, { merge: true });
  } catch (err) {
    console.error("Failed saving agent to Firebase:", err);
    throw err;
  }
}

export async function saveAgentsOrderToFirebase(agents: Agent[]): Promise<void> {
  try {
    const batch = writeBatch(db);
    agents.forEach((agent, index) => {
      const agentDoc = doc(db, AGENTS_COLLECTION, agent.id);
      batch.set(agentDoc, { order: index + 1 }, { merge: true });
    });
    await batch.commit();
  } catch (err) {
    console.warn("Failed batch saving agents order to Firebase:", err);
  }
}

export async function deleteAgentFromFirebase(agentId: string): Promise<void> {
  try {
    const agentDoc = doc(db, AGENTS_COLLECTION, agentId);
    await deleteDoc(agentDoc);
  } catch (err) {
    console.error("Failed deleting agent from Firebase:", err);
    throw err;
  }
}

// ---------------------------------------------------------------------------
// SITE CONTENT CMS
// ---------------------------------------------------------------------------

const SITE_CONTENT_DOC_ID = "main";

export async function getSiteContentFromFirebase(): Promise<SiteContent | null> {
  try {
    const docRef = doc(db, SITE_CONTENT_COLLECTION, SITE_CONTENT_DOC_ID);
    const snap = await getDoc(docRef);
    if (snap.exists()) {
      return snap.data() as SiteContent;
    }
  } catch (err) {
    console.warn("Could not fetch site content from Firebase:", err);
  }
  return null;
}

export async function saveSiteContentToFirebase(content: SiteContent): Promise<void> {
  try {
    const docRef = doc(db, SITE_CONTENT_COLLECTION, SITE_CONTENT_DOC_ID);
    await setDoc(docRef, content, { merge: true });
  } catch (err) {
    console.error("Failed saving site content to Firebase:", err);
    throw err;
  }
}

// ---------------------------------------------------------------------------
// INQUIRIES & LEADS
// ---------------------------------------------------------------------------

export interface InquiryData {
  id?: string;
  name: string;
  phone: string;
  email?: string;
  propertyId?: string;
  propertyTitle?: string;
  message?: string;
  type: 'consultation' | 'inspection' | 'contact';
  createdAt: string;
  status: 'new' | 'in_progress' | 'completed';
  emailStatus?: 'sent' | 'failed' | 'pending';
  emailError?: string;
}

export async function submitInquiryToFirebase(inquiry: Omit<InquiryData, 'createdAt' | 'status'> & { emailStatus?: 'sent' | 'failed' | 'pending'; emailError?: string }): Promise<string> {
  try {
    const rawData: Record<string, any> = {
      name: inquiry.name || '',
      phone: inquiry.phone || '',
      email: inquiry.email || '',
      propertyId: inquiry.propertyId || '',
      propertyTitle: inquiry.propertyTitle || '',
      message: inquiry.message || '',
      type: inquiry.type || 'contact',
      createdAt: new Date().toISOString(),
      status: 'new',
      emailStatus: inquiry.emailStatus || 'pending',
      emailError: inquiry.emailError || ''
    };

    // Remove any undefined keys to ensure Firestore addDoc never throws
    const cleanInquiry: Record<string, any> = {};
    for (const [key, value] of Object.entries(rawData)) {
      if (value !== undefined) {
        cleanInquiry[key] = value;
      }
    }

    const docRef = await addDoc(collection(db, INQUIRIES_COLLECTION), cleanInquiry);
    return docRef.id;
  } catch (err) {
    console.error("Failed submitting inquiry to Firebase:", err);
    throw err;
  }
}

export async function getInquiriesFromFirebase(): Promise<InquiryData[]> {
  try {
    const querySnapshot = await getDocs(collection(db, INQUIRIES_COLLECTION));
    const inquiries: InquiryData[] = [];
    querySnapshot.forEach((doc) => {
      inquiries.push({ id: doc.id, ...(doc.data() as InquiryData) });
    });
    // Sort newest first
    return inquiries.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  } catch (err) {
    console.warn("Could not fetch inquiries from Firebase:", err);
    return [];
  }
}

export async function updateInquiryStatusInFirebase(
  inquiryId: string, 
  status: 'new' | 'in_progress' | 'completed',
  extra?: { emailStatus?: 'sent' | 'failed' | 'pending'; emailError?: string }
): Promise<void> {
  try {
    const inquiryDoc = doc(db, INQUIRIES_COLLECTION, inquiryId);
    const updates: Record<string, any> = { status };
    if (extra?.emailStatus) updates.emailStatus = extra.emailStatus;
    if (extra?.emailError !== undefined) updates.emailError = extra.emailError;
    await updateDoc(inquiryDoc, updates);
  } catch (err) {
    console.error("Failed updating inquiry status in Firebase:", err);
    throw err;
  }
}

export async function deleteInquiryFromFirebase(inquiryId: string): Promise<void> {
  try {
    const inquiryDoc = doc(db, INQUIRIES_COLLECTION, inquiryId);
    await deleteDoc(inquiryDoc);
  } catch (err) {
    console.error("Failed deleting inquiry from Firebase:", err);
    throw err;
  }
}
